import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function TermsPage() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-mono">
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-10">
          <img
            src={`${basePath}/pa-icon.jpg`}
            alt="Podcasts Abbreviated"
            className="h-8 w-8 rounded-sm object-cover"
          />
          <span className="font-mono font-bold tracking-tight uppercase text-sm text-foreground">
            Podcasts <span className="text-primary">Abbreviated</span>
          </span>
        </div>

        <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground text-sm mb-10">Last updated: April 25, 2025</p>

        <div className="prose prose-invert max-w-none space-y-8 text-sm leading-7 text-muted-foreground">

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Podcasts Abbreviated (the "Service"), you agree to be bound by
              these Terms of Service. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. Description of Service</h2>
            <p>
              Podcasts Abbreviated provides tools to transcribe podcast audio, generate concise
              summaries, and optionally publish clips to connected social media accounts such as
              YouTube and LinkedIn. The Service is offered on a subscription or pay-per-use basis.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">3. Eligibility</h2>
            <p>
              You must be at least 13 years old to use the Service. By using the Service, you
              represent that you meet this requirement and have the authority to agree to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">4. Your Content and Licenses</h2>
            <p className="mb-3">
              You retain ownership of any audio, transcripts, summaries, and other content you
              submit or that the Service generates on your behalf. By using the Service, you grant
              us a limited license to process, store, and transmit your content as necessary to
              deliver the Service.
            </p>
            <p>
              You represent that you have the rights to submit all content you provide and that
              doing so does not violate any third-party rights or applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">5. Connected Social Accounts</h2>
            <p className="mb-3">
              When you connect a YouTube or LinkedIn account, you authorize us to publish content
              to that account only when you explicitly initiate an upload. You may revoke this
              authorization at any time by disconnecting the account within the Service or through
              the respective platform's account settings.
            </p>
            <p>
              For YouTube connections, we request access only to the scopes required for uploading
              and managing the videos you submit. We do not access your subscribers, analytics,
              comments, or any other channel data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">6. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Submit content you do not have rights to process or publish</li>
              <li>Use the Service to violate any applicable law or regulation</li>
              <li>Attempt to reverse-engineer, scrape, or abuse the Service infrastructure</li>
              <li>Circumvent any access controls, usage limits, or billing mechanisms</li>
              <li>Use the Service to generate or distribute harmful, illegal, or misleading content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">7. Payments and Subscriptions</h2>
            <p>
              Paid features are billed through Stripe. All fees are stated in US dollars and are
              non-refundable except as required by law or stated in our refund policy. We reserve
              the right to change pricing with reasonable notice.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. Disclaimers</h2>
            <p>
              The Service is provided "as is" without warranties of any kind, express or implied.
              We do not guarantee that transcripts or summaries will be error-free, complete, or
              suitable for any particular purpose. AI-generated content may contain inaccuracies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, we shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages arising from your use of
              the Service, even if advised of the possibility of such damages.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">10. Termination</h2>
            <p>
              We may suspend or terminate your account if you violate these Terms or for any other
              reason at our discretion with reasonable notice. You may close your account at any
              time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">11. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. Continued use of the Service after
              changes take effect constitutes acceptance of the revised Terms. We will provide
              notice of material changes via email or an in-app notification.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which we operate, without
              regard to conflict-of-law principles.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">13. Contact</h2>
            <p>
              For questions about these Terms, visit our{" "}
              <Link href="/help" className="text-primary hover:underline">Help page</Link>{" "}
              or use the chat support widget to reach us directly.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
